import { simplifySqrtLatex, evaluateSqrtString } from "./sqrtUtils";
import gcd from "./gcd";

function parseFractionPart(input) {
  const fracMatch = input.match(/\\dfrac{(.+)}{(.+)}/);
  if (fracMatch) {
    const numerator = fracMatch[1].replace(/\\sqrt{(.+)}/g, "√$1");
    const denominator = parseFloat(fracMatch[2]);
    const parsedNumerator = parseFractionPart(numerator);
    return {
      sqrt: parsedNumerator.sqrt,
      constant: parsedNumerator.constant,
      denominator,
    };
  }

  const sqrtMatch = input.match(/(?:\\sqrt{|\b√)(\d+)\}?/);
  if (sqrtMatch) {
    return {
      sqrt: parseInt(sqrtMatch[1]),
      constant: 1,
      denominator: 1,
    };
  }

const cleaned = input.replace(/\s/g, "");
if (/^√\d+$/.test(cleaned)) {
  const sqrt = parseInt(cleaned.replace("√", ""));
  return { sqrt, constant: 1, denominator: 1 };
}

const constant = parseFloat(cleaned);
return {
  sqrt: null,
  constant: isNaN(constant) ? 0 : constant,
  denominator: 1,
};

}

function stringifyTerm({ constant, sqrt }) {
  if (constant === 0) return null;
  if (sqrt === null) return `${constant}`;
  if (constant === 1) return `\\sqrt{${sqrt}}`;
  if (constant === -1) return `-\\sqrt{${sqrt}}`;
  return `${constant}\\sqrt{${sqrt}}`;
}

function addFractionsWithSqrt({ left, right, operation }) {
  const opSign = operation === "-" ? -1 : 1;

  const leftVal = parseFractionPart(left.numerator);
  const rightVal = parseFractionPart(right.numerator);

  const leftDen = left.denominator ?? leftVal.denominator ?? 1;
  const rightDen = right.denominator ?? rightVal.denominator ?? 1;

  const commonDenominator = leftDen === rightDen ? leftDen : leftDen * rightDen;

  const leftMultiplier = commonDenominator / leftDen;
  const rightMultiplier = commonDenominator / rightDen;

  const scaledLeft = {
    sqrt: leftVal.sqrt,
    constant: leftVal.constant * leftMultiplier,
  };

  const scaledRight = {
    sqrt: rightVal.sqrt,
    constant: opSign * rightVal.constant * rightMultiplier,
  };

  const terms = [];
  const canCombine = scaledLeft.sqrt !== null && scaledLeft.sqrt === scaledRight.sqrt;

  if (canCombine) {
    const total = scaledLeft.constant + scaledRight.constant;
    if (total !== 0) {
      terms.push({ sqrt: scaledLeft.sqrt, constant: total });
    }
  } else {
    if (scaledLeft.constant !== 0) terms.push(scaledLeft);
    if (scaledRight.constant !== 0) terms.push(scaledRight);
  }

  const numeratorLatex = terms
    .map(stringifyTerm)
    .filter(Boolean)
    .join(" + ")
    .replace(/\+\s*-/g, "- ");

let latex;
if (commonDenominator === 1) {
  latex = numeratorLatex;
} else {
  // wyciągamy minus z przodu, jeśli cały licznik zaczyna się od minusa
  const hasGlobalMinus = numeratorLatex.trim().startsWith("-");
  if (hasGlobalMinus) {
    latex = `-\\frac{${numeratorLatex.trim().slice(1)}}{${commonDenominator}}`;
  } else {
    latex = `\\frac{${numeratorLatex}}{${commonDenominator}}`;
  }
}


  let simplified = null;

  // uproszczenie tylko dla liczb bez pierwiastków
  if (terms.every((t) => t.sqrt === null)) {
    const raw = terms.reduce((sum, t) => sum + t.constant, 0);
    const divisor = gcd(raw, commonDenominator);
    const num = raw / divisor;
    const den = commonDenominator / divisor;

if (den === 1) {
  simplified = `${num}`;
  latex = `${num}`;
} else {
  simplified = `\\frac{${Math.abs(num)}}{${den}}`;
  latex = num < 0 ? `-\\frac{${Math.abs(num)}}{${den}}` : simplified;
}


  }

  const numericValue =
    terms.reduce((sum, t) => sum + t.constant * (t.sqrt ? Math.sqrt(t.sqrt) : 1), 0) /
    commonDenominator;

latex = latex.replace(/\\frac/g, "\\dfrac");
simplified = simplified?.replace(/\\frac/g, "\\tfrac");

return {
  latex,
  simplifiedLatex: simplified ?? latex,
  value: numericValue,
};

}

export default addFractionsWithSqrt;
